import { api } from "../api/client";

/**
 * The session, as the API describes it (spec §4.5, §4.6).
 *
 * These types mirror the responses of `/api/auth/*` by hand. There is no generated client and no
 * shared schema package, which is a deliberate choice at this size rather than an omission: the API
 * has five auth routes, the duplication is a few lines, and the alternative — a build step producing
 * types from the handlers — is machinery to maintain before there is enough surface to justify it.
 * The show API in `shows/api.ts` is mirrored the same way, and is the surface to watch: as it grows,
 * that trade changes.
 */

export interface AccountSummary {
  id: string;
  email: string;
  displayName: string | null;
  /** Spec §4.3's required details are supplied. False between verifying a code and giving a name. */
  profileComplete: boolean;
}

/**
 * Spec §3's two staff roles. Empty for an ordinary customer, which on this hostname means
 * somebody with no panel access at all.
 *
 * Hand-written against `packages/core/src/auth/roles.ts`, like every other type in this panel.
 * `useMayRunShows` in `auth/roles.ts` is what reads it, and the server is what enforces it.
 */
export type Role = "moderator" | "host";

export type SessionState =
  | { signedIn: false; reason?: string }
  | {
      signedIn: true;
      account: AccountSummary;
      roles: Role[];
      needsProfile: boolean;
      /** Whether this session may use the panel. Rendered from, never enforced by — the API checks again. */
      panel: boolean;
    };

export function readSession(): Promise<SessionState> {
  return api.get<SessionState>("/api/auth/session");
}

export interface CodeRequested {
  sent: true;
  email: string;
  codeLength: number;
}

/**
 * `R-SEC-4` — this resolves identically whether or not the address has an account, because the API
 * never looks. There is nothing in the response for the client to branch on, which is the point.
 */
export function requestCode(email: string): Promise<CodeRequested> {
  return api.post<CodeRequested>("/api/auth/code", { email });
}

export interface Verified {
  signedIn: true;
  account: AccountSummary;
  roles: Role[];
  needsProfile: boolean;
  expiresAt: string;
}

export function verifyCode(email: string, code: string): Promise<Verified> {
  return api.post<Verified>("/api/auth/verify", { email, code });
}

export function completeProfile(
  displayName: string,
  marketingConsent: boolean,
): Promise<{ account: AccountSummary; needsProfile: boolean }> {
  return api.post("/api/auth/profile", {
    displayName,
    // Spec §4.3 requires an explicit acceptance, so the client has to say so rather than the server
    // inferring it from the request having been made at all.
    acceptTerms: true,
    marketingConsent,
  });
}

export function signOut(): Promise<unknown> {
  return api.post("/api/auth/signout");
}
